import * as THREE from "three";
import { CUSHION_LIKE_COMPONENT_TYPES } from "../../utils/constants.js";
import {
  getActiveAttachmentPoints,
  resolveCollisionSpec,
  isPointInsideFurnitureXZ,
} from "../../utils/geometry.js";
import { VARIANT_TWEEN_MS, VARIANT_ROUTE_STAGE_MS } from "./tweenTiming.js";

// switchVariant and the phases it runs in order; installed on FurnitureElement.prototype.
export const variantSwitchMethods = {
  /**
   * Switch to a different variant. Tweens compositeMesh transform AND any
   * named child nodes referenced by variant.nodeAnimations (HingePivot, etc.)
   * in one pass. Rebuilds attachment points and recomputes the bounding box.
   *
   * If the target variant declares a `clearanceBox` and a CollisionSystem is
   * supplied, the switch is rejected when the clearance volume would intrude
   * on another module. Returns true on success, false on rejection.
   *
   * @param {number} variantIndex - Index into furnitureData.properties.variants
   * @param {{ checkOpenClearance: Function }|null} [collisionSystem]
   * @param {{ animate?: boolean }} [opts] animate:false settles the tween immediately
   * @returns {boolean}
   */
  switchVariant(variantIndex, collisionSystem = null, opts = {}) {
    const variants = this.furnitureData?.properties?.variants;
    if (!variants || variantIndex < 0 || variantIndex >= variants.length) return false;
    if (variantIndex === this.variantIndex) return false;

    if (this._variantClearanceBlocked(variants, variantIndex, collisionSystem)) return false;

    const fromVariant = variants[this.variantIndex] ?? null;
    const toVariant = variants[variantIndex];
    this.variantIndex = variantIndex;

    const originalFurnitureData = this.furnitureData;

    // Mirror per-variant attachmentPoints/materialZones into the property-level
    // fallbacks so legacy call sites (e.g. FurnitureManager.getMaterialZones,
    // anything still reading properties.attachmentPoints without a variant fallback)
    // see the active-variant data. Shallow-cloned to avoid mutating the shared catalog.
    this.furnitureData = {
      ...this.furnitureData,
      properties: {
        ...this.furnitureData.properties,
        attachmentPoints: toVariant.attachmentPoints || this.furnitureData.properties.attachmentPoints || [],
        ...(toVariant.materialZones ? { materialZones: toVariant.materialZones } : {}),
      },
    };

    // Hide non-module attached components (cushions/panels) when entering a variant
    // with no APs — they have nowhere valid to sit during e.g. an "open storage" pose.
    this._setAttachedComponentsHidden((toVariant.attachmentPoints?.length ?? 0) === 0);

    const compositeFrom = this.compositeMesh
      ? {
          fromPos: this.compositeMesh.position.clone(),
          fromRotY: this.compositeMesh.rotation.y,
        }
      : null;

    // Restore in-flight fade clones before building the next tween — otherwise
    // we'd capture the temporary clone as the "original" material and leak it.
    this._cancelPendingFades();

    const nodeStages = this._buildNodeStages(fromVariant, toVariant);
    const compositeStages = this._buildCompositeStages(variants, variantIndex, toVariant, compositeFrom);

    const chainTail = this._startTweenChain(compositeStages, nodeStages);
    // Nothing to animate — apply any composite snap (a no-op without one).
    if (!chainTail) this._applyVariantCompositeTransform(variantIndex);

    const childLinks = this._snapshotChildLinks();

    // Rebuild the APs; shouldShowAttachmentPoint then runs on the fresh markers.
    for (const ap of this.attachmentPoints) {
      ap.hide();
      ap.dispose();
    }
    this.attachmentPoints = [];
    this.createAttachmentPoints();

    this._rebindChildLinks(childLinks);
    this._tweenComponentsToNewAPs(childLinks, compositeStages, chainTail, variants, originalFurnitureData);
    this._collectOrphanedCushions(childLinks);

    // animate:false settles the tween in one step — an offscreen capture never advances it.
    if (opts.animate === false) while (this._tween) this.update(1e6);

    this.refreshAttachmentPointVisibility();
    this.computeBoundingBoxFull();
    return true;
  },

  // Clearance guard: refuse the switch if it would intrude on another module.
  _variantClearanceBlocked(variants, variantIndex, collisionSystem) {
    // 1. Explicit clearanceBox (e.g. Open variant)
    const clearance = variants[variantIndex]?.clearanceBox;
    if (clearance?.size && collisionSystem?.checkOpenClearance) {
      if (collisionSystem.checkOpenClearance(this, clearance)) return true;
    }
    // 2. Target variant's collisionBoxes — catch cases like Straight whose larger
    //    footprint would overlap adjacent modules even without a clearanceBox.
    if (collisionSystem?.checkOpenClearance) {
      const targetSpec = resolveCollisionSpec(this.furnitureData?.properties, variantIndex);
      for (const cb of targetSpec?.boxes ?? []) {
        if (collisionSystem.checkOpenClearance(this, cb)) return true;
      }
    }
    return false;
  },

  _setAttachedComponentsHidden(wantHideComponents) {
    const components = this.attachedChildren.filter(
      c => c.furnitureData?.componentType !== 'module'
    );
    components.forEach(child => {
      if (!child.mesh) return;
      child.mesh.visible = !wantHideComponents;
      child.mesh.traverse(c => {
        if (!c.isMesh) return;
        if (wantHideComponents) {
          if (c.userData._savedRaycast === undefined) {
            c.userData._savedRaycast = c.raycast;
            c.raycast = () => {};
          }
        } else if (c.userData._savedRaycast !== undefined) {
          c.raycast = c.userData._savedRaycast;
          delete c.userData._savedRaycast;
        }
      });
    });
  },

  // Composite stages; the last is merged with the node settle stage.
  _buildCompositeStages(variants, variantIndex, toVariant, compositeFrom) {
    const compositeStages = [];
    if (this.compositeMesh && this.mesh?.parent) {
      const targetOffset = toVariant.compositeOffset || { x: 0, y: 0, z: 0 };
      const targetRotY = THREE.MathUtils.degToRad(toVariant.compositeRotationY ?? 0);
      const rotDelta = Math.abs(targetRotY - compositeFrom.fromRotY);

      // Right↔Left spans 180° — route through a neutral intermediate (rotY≈90°)
      // so the arm doesn't swing through a misleading midpoint. Skip variants
      // that would route into themselves and prefer the first match.
      const throughIdx = rotDelta > Math.PI * 0.7
        ? variants.findIndex((v, i) => i !== variantIndex && Math.abs((v.compositeRotationY ?? 0) - 90) < 1)
        : -1;

      if (throughIdx >= 0) {
        const throughVariant = variants[throughIdx];
        const throughOffset = throughVariant.compositeOffset || { x: 0, y: 0, z: 0 };
        const throughRotY = THREE.MathUtils.degToRad(throughVariant.compositeRotationY ?? 0);
        compositeStages.push({
          durationMs: VARIANT_ROUTE_STAGE_MS,
          composite: {
            fromPos: compositeFrom.fromPos,
            toPos: new THREE.Vector3(throughOffset.x, throughOffset.y, throughOffset.z),
            fromRotY: compositeFrom.fromRotY,
            toRotY: throughRotY,
          },
        });
        compositeStages.push({
          durationMs: VARIANT_ROUTE_STAGE_MS,
          composite: {
            fromPos: new THREE.Vector3(throughOffset.x, throughOffset.y, throughOffset.z),
            toPos: new THREE.Vector3(targetOffset.x, targetOffset.y, targetOffset.z),
            fromRotY: throughRotY,
            toRotY: targetRotY,
          },
        });
      } else {
        compositeStages.push({
          durationMs: toVariant.transitionMs ?? VARIANT_TWEEN_MS,
          composite: {
            fromPos: compositeFrom.fromPos,
            toPos: new THREE.Vector3(targetOffset.x, targetOffset.y, targetOffset.z),
            fromRotY: compositeFrom.fromRotY,
            toRotY: targetRotY,
          },
        });
      }
      this.compositeMesh.visible = toVariant.compositeVisible !== false;
    }
    return compositeStages;
  },

  // Snapshot existing child→AP links before destroying APs so we can re-bind
  // each child to the equivalent AP on the rebuilt list. We also persist the
  // metadata on the child itself (`_lastLinkMeta`) so it survives a transit
  // through an empty-AP variant — otherwise switching closed→open nulls
  // `attachedTo` (no AP to bind to), and switching back open→closed loses the
  // reconnection info, leaving the child orphaned and + markers exposed.
  _snapshotChildLinks() {
    return this.attachedChildren.map(child => {
      const live = child.attachedTo
        ? {
            side: child.attachedTo.side,
            localOffset: child.attachedTo.localOffset?.clone() ?? null,
            componentType: child.furnitureData?.componentType ?? null,
          }
        : null;
      if (live) child._lastLinkMeta = live;
      const meta = live ?? child._lastLinkMeta ?? null;
      return {
        child,
        side: meta?.side ?? null,
        localOffset: meta?.localOffset?.clone() ?? null,
        componentType: meta?.componentType ?? child.furnitureData?.componentType ?? null,
      };
    });
  },

  // Re-bind children to the new APs by matching side (+ closest local offset
  // when multiple APs share a side, e.g. choice-b9 with two panel APs per side).
  // For module children, mark only the same-side APs whose world position falls
  // within the child's footprint — mirrors attachTo()'s precise occupancy so a
  // small child doesn't block this module's other free APs after a variant change.
  _rebindChildLinks(childLinks) {
    const _apWorldPosScratch = new THREE.Vector3();
    for (const link of childLinks) {
      if (!link.side) continue;
      if (link.componentType === 'module') {
        const moduleAPs = this.attachmentPoints.filter(ap =>
          ap.side === link.side && ap.acceptsComponentTypes.includes('module')
        );
        if (moduleAPs.length > 0) {
          this.mesh.updateMatrixWorld(true);
          link.child.mesh?.updateMatrixWorld(true);
          let firstCovered = null;
          for (const ap of moduleAPs) {
            ap.getWorldPosition(_apWorldPosScratch);
            if (isPointInsideFurnitureXZ(link.child, _apWorldPosScratch.x, _apWorldPosScratch.z, 0.02)) {
              ap.setOccupied(link.child);
              if (!firstCovered) firstCovered = ap;
            }
          }
          link.child.attachedTo = firstCovered ?? moduleAPs[0];
        } else {
          link.child.attachedTo = null;
        }
        continue;
      }
      const candidates = this.attachmentPoints.filter(ap =>
        ap.side === link.side &&
        !ap.isOccupied &&
        (link.componentType ? ap.acceptsComponentTypes.includes(link.componentType) : true)
      );
      let best = candidates[0] ?? null;
      if (best && candidates.length > 1 && link.localOffset) {
        let bestDist = Infinity;
        for (const ap of candidates) {
          const d = ap.localOffset.distanceTo(link.localOffset);
          if (d < bestDist) { bestDist = d; best = ap; }
        }
      }
      if (best) {
        link.child.attachedTo = best;
        best.setOccupied(link.child);
      } else {
        link.child.attachedTo = null;
      }
    }
  },

  // Animate component (cushion) positions and rotations to match their new AP.
  // The cushion rotates by the same Y-axis delta as the composite (backrest) mesh so it
  // always sits correctly oriented on top of the backrest after any variant switch.
  _tweenComponentsToNewAPs(childLinks, compositeStages, chainTail, variants, originalFurnitureData) {
    if (this._tween) {
      // Components land on the chain tail; a routed composite splits them across
      // its two stages instead. Composite stages are contiguous, so the total Y
      // rotation is the end-to-end delta regardless of how many there are.
      const tail = chainTail ?? this._tween;
      const routed = compositeStages.length > 1;
      const compositeRotDelta = compositeStages.length
        ? compositeStages[compositeStages.length - 1].composite.toRotY - compositeStages[0].composite.fromRotY
        : 0;

      const componentEntries = [];
      for (const link of childLinks) {
        if (!link.side || link.componentType === 'module') continue;
        const newAP = link.child.attachedTo;
        if (!newAP || !link.child.mesh) continue;
        const toRotY = newAP.componentRotationY !== undefined
          ? newAP.componentRotationY
          : link.child.mesh.rotation.y + compositeRotDelta;
        componentEntries.push({
          mesh: link.child.mesh,
          side: newAP.side,
          componentType: link.componentType,
          fromPos: link.child.mesh.position.clone(),
          toPos: newAP.localPosition.clone(),
          fromRotY: link.child.mesh.rotation.y,
          toRotY,
        });
      }

      if (componentEntries.length > 0) {
        if (routed) {
          // Two-stage composite tween (e.g. Right↔Left through Straight). Split position across
          // both stages using the through-variant's AP position; split rotation by each stage's delta.
          const stage1RotDelta = compositeStages[0].composite.toRotY - compositeStages[0].composite.fromRotY;
          const tr = this._tween.composite;
          const throughVariantIdx = tr
            ? variants.findIndex((v) => {
                const tp = v.compositeOffset || { x: 0, y: 0, z: 0 };
                return Math.abs(tp.x - tr.toPos.x) < 0.001 &&
                       Math.abs(tp.y - tr.toPos.y) < 0.001 &&
                       Math.abs(tp.z - tr.toPos.z) < 0.001;
              })
            : -1;
          const throughAPsData = throughVariantIdx >= 0
            ? getActiveAttachmentPoints(originalFurnitureData, throughVariantIdx)
            : [];

          const stage1Entries = componentEntries.map((c) => {
            const throughAPData = throughAPsData.find(ap =>
              ap.side === c.side &&
              (!c.componentType || (ap.accepts ?? []).includes(c.componentType))
            ) ?? throughAPsData.find(ap => ap.side === c.side) ?? null;
            const throughPos = throughAPData
              ? new THREE.Vector3(throughAPData.offset.x, throughAPData.offset.y, throughAPData.offset.z)
              : c.toPos.clone();
            const throughRotY = throughAPData?.rotationY !== undefined
              ? THREE.MathUtils.degToRad(throughAPData.rotationY)
              : c.fromRotY + stage1RotDelta;
            return { mesh: c.mesh, fromPos: c.fromPos.clone(), toPos: throughPos, fromRotY: c.fromRotY, toRotY: throughRotY };
          });

          this._tween.components = stage1Entries;
          tail.components = componentEntries.map((c, i) => ({
            mesh: c.mesh,
            fromPos: stage1Entries[i].toPos.clone(),
            toPos: c.toPos,
            fromRotY: stage1Entries[i].toRotY,
            toRotY: c.toRotY,
          }));
        } else {
          tail.components = componentEntries;
        }
      }
    }
  },

  // Collect cushion children that lost their AP in the new variant so the caller
  // (FurnitureManager) can properly delete them. Remove from attachedChildren now
  // so they don't interfere with AP visibility or subsequent rebinds.
  _collectOrphanedCushions(childLinks) {
    this._pendingDeleteComponents = [];
    for (const link of childLinks) {
      if (link.componentType === 'module') continue;
      if (link.child.attachedTo === null &&
          CUSHION_LIKE_COMPONENT_TYPES.has(link.child.furnitureData?.componentType)) {
        const idx = this.attachedChildren.indexOf(link.child);
        if (idx !== -1) this.attachedChildren.splice(idx, 1);
        this._pendingDeleteComponents.push(link.child);
      }
    }
  },
};
